// Card de entrega da página de produto.
//
// Sete estados, e o rodapé com o local ativo aparece em cinco deles. No protótipo o rodapé
// some justamente em "sem cobertura" e "falha", tirando a referência do local exatamente
// quando o cliente mais precisa dela — aqui ele fica.
//
// Cobertura e disponibilidade são dimensões independentes: produto sem estoque na farmácia
// que cobre o CEP não exibe prazo, mas o botão de compra continua governado por util/buy.js,
// que este componente não toca.

import React, { useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import MaskedInput from "react-text-mask";
import { FiAlertTriangle, FiRefreshCw } from "react-icons/fi";
import { Storefront, LocalShipping, Speed } from "@material-ui/icons";

import DeliveryLocationPanel from "../DeliveryLocationPanel";
import {
  Status,
  Cobertura,
  Creators as DeliveryContextActions
} from "../../store/ducks/deliveryContext";
import { Creators as StoreByUserActions } from "../../store/ducks/storeByUser";
import { getAddressByCEPCached } from "../../services/cep";
import { normalizeCep } from "../../util/deliveryContextStorage";
import { formatCEP } from "../../util/formatCEP";
import { formatPrice } from "../../util/format";
import { cepMask } from "../../util/mask";
import {
  Card,
  FaixaConflito,
  Linha,
  IconBadge,
  LinhaTitulo,
  LinhaDetalhe,
  NotaCentro,
  Aviso,
  BotaoContornado,
  LinkAcao,
  Rodape,
  RodapeTexto,
  LinkAlterar,
  BlocoCarregando,
  ConsultaBloco,
  ConsultaRotulo,
  ConsultaLinha,
  ConsultaInput,
  ConsultaBotao,
  ConsultaApoio,
  MensagemErro
} from "./styles";

export default function DeliveryCard({ estimativa }) {
  const dispatch = useDispatch();
  const alterarRef = useRef(null);
  const [painelAberto, setPainelAberto] = useState(false);
  const [valorCep, setValorCep] = useState("");
  const [consultando, setConsultando] = useState(false);

  const { status, cobertura, cep, conflitoFarmacia, farmaciaTravada, erroCep } = useSelector(
    (state) => state.deliveryContext
  );

  const temLocal = !!cep;

  async function consultarCep() {
    const digitos = normalizeCep(valorCep);

    if (!digitos) {
      dispatch(DeliveryContextActions.cepInvalido("Informe um CEP com 8 dígitos."));
      return;
    }

    setConsultando(true);

    try {
      const { data } = await getAddressByCEPCached(digitos);

      if (!data || data.erro) {
        dispatch(DeliveryContextActions.cepInvalido());
        return;
      }

      dispatch(
        DeliveryContextActions.applyCep(digitos, {
          cidade: data.localidade || null,
          uf: data.uf || null,
          bairro: data.bairro || null
        })
      );

      // A alteração partiu do card, fora do campo de visão do seletor da barra superior.
      dispatch(DeliveryContextActions.destacarSeletor());
    } catch (_) {
      dispatch(DeliveryContextActions.cepInvalido("Não foi possível consultar o CEP."));
    } finally {
      setConsultando(false);
    }
  }

  function liberarEscolhaAutomatica() {
    // A variante sem reload: o contexto reassume a farmácia eleita por onLockedPharmacyChanged.
    dispatch(StoreByUserActions.setStore(null, false));
  }

  const rodape = temLocal && (
    <Rodape>
      <RodapeTexto>Entregando em {formatCEP(cep)}</RodapeTexto>
      <LinkAlterar
        ref={alterarRef}
        type="button"
        onClick={() => setPainelAberto((atual) => !atual)}
        aria-haspopup="dialog"
        aria-expanded={painelAberto}
        aria-controls={painelAberto ? "painel-entrega-card" : undefined}
      >
        alterar
      </LinkAlterar>
    </Rodape>
  );

  const painel = painelAberto && (
    <DeliveryLocationPanel
      anchorRef={alterarRef}
      align="right"
      id="painel-entrega-card"
      onClose={() => setPainelAberto(false)}
    />
  );

  // 1. Sem local. Não houve referência visual no protótipo, onde o CEP nunca esvazia.
  if (status === Status.INDEFINIDO || status === Status.CEP_INVALIDO || !temLocal) {
    return (
      <Card>
        <ConsultaBloco>
          <ConsultaRotulo htmlFor="card-entrega-cep">Consultar frete</ConsultaRotulo>
          <ConsultaLinha>
            <MaskedInput
              mask={cepMask}
              id="card-entrega-cep"
              value={valorCep}
              onChange={(e) => {
                setValorCep(e.target.value);
                if (erroCep) dispatch(DeliveryContextActions.clearCepError());
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  consultarCep();
                }
              }}
              placeholder="Ex: _____-___"
              aria-invalid={erroCep ? "true" : "false"}
              aria-describedby={erroCep ? "card-entrega-cep-erro" : undefined}
              render={(ref, props) => (
                <ConsultaInput ref={(input) => ref(input)} invalido={!!erroCep} {...props} />
              )}
            />
            <ConsultaBotao type="button" onClick={consultarCep} disabled={consultando}>
              {consultando ? "..." : "Calcular"}
            </ConsultaBotao>
          </ConsultaLinha>
          {erroCep && <MensagemErro id="card-entrega-cep-erro">{erroCep}</MensagemErro>}
          <ConsultaApoio>Informe seu CEP para ver prazo e frete</ConsultaApoio>
        </ConsultaBloco>
      </Card>
    );
  }

  // 2. Carregando.
  if (status === Status.RESOLVENDO) {
    return (
      <Card>
        <BlocoCarregando aria-live="polite" aria-label="Calculando prazo de entrega" />
      </Card>
    );
  }

  // 7. Falha na consulta. Distinta de ausência de atendimento: a API responde sempre 200
  // com indicador no corpo, então falha só acontece por rede ou servidor.
  if (status === Status.FALHA) {
    return (
      <>
        <Card>
          <Aviso>
            <FiAlertTriangle size={16} aria-hidden="true" />
            Não foi possível consultar o prazo de entrega agora.
          </Aviso>
          <LinkAcao
            type="button"
            onClick={() => dispatch(DeliveryContextActions.resolveRequest())}
          >
            <FiRefreshCw size={14} aria-hidden="true" />
            Tentar novamente
          </LinkAcao>
          {rodape}
        </Card>
        {painel}
      </>
    );
  }

  // 6. Sem cobertura. LOCALIDADE_INCOMPLETA usa a mesma moldura com texto próprio: é CEP que
  // resolveu sem bairro, e pedir o endereço completo não é o mesmo que não haver entrega.
  if (status === Status.NAO_ATENDIDO) {
    const incompleta = cobertura === Cobertura.LOCALIDADE_INCOMPLETA;

    return (
      <>
        <Card atencao>
          <Aviso>
            <FiAlertTriangle size={16} aria-hidden="true" />
            {incompleta
              ? "Informe o endereço completo para calcularmos o prazo."
              : `Ainda não entregamos em ${formatCEP(cep)}.`}
          </Aviso>
          <BotaoContornado type="button" onClick={() => setPainelAberto((a) => !a)}>
            Informar outro CEP
          </BotaoContornado>
          {rodape}
        </Card>
        {painel}
      </>
    );
  }

  // Conflito: a farmácia travada não cobre o CEP. Prazo suprimido, farmácia mantida. Cobrir
  // sem ser a mais rápida NÃO cai aqui — o prazo é o dela, e foi isso que o cliente escolheu.
  if (conflitoFarmacia) {
    return (
      <>
        <FaixaConflito>
          <FiAlertTriangle size={16} aria-hidden="true" />
          <span>
            {farmaciaTravada?.nome_fantasia
              ? `A farmácia ${farmaciaTravada.nome_fantasia} não atende este CEP.`
              : "A farmácia escolhida não atende este CEP."}
          </span>
        </FaixaConflito>
        <Card>
          <LinkAcao type="button" onClick={liberarEscolhaAutomatica}>
            Liberar escolha automática
          </LinkAcao>
          {rodape}
        </Card>
        {painel}
      </>
    );
  }

  const { entregaExpressa, entregaNormal, retirada, origem } = estimativa || {};

  // 3, 4 e 5. Atendido. Sem estoque na farmácia que cobre, as duas linhas somem e resta a
  // referência do local — cobertura e disponibilidade são dimensões independentes.
  return (
    <>
      <Card>
        {origem === "GAM" && (
          <NotaCentro>Este item vem do nosso centro de distribuição.</NotaCentro>
        )}

        {entregaExpressa && (
          <Linha>
            <IconBadge>
              <Speed style={{ fontSize: "22px" }} />
            </IconBadge>
            <LinhaTitulo>Entrega expressa, {entregaExpressa.texto}</LinhaTitulo>
            <LinhaDetalhe>
              {entregaExpressa.custo !== null && entregaExpressa.custo !== undefined
                ? entregaExpressa.custo > 0
                  ? formatPrice(entregaExpressa.custo)
                  : "Frete grátis"
                : null}
            </LinhaDetalhe>
          </Linha>
        )}

        {entregaNormal && (
          <Linha>
            <IconBadge>
              <LocalShipping style={{ fontSize: "22px" }} />
            </IconBadge>
            <LinhaTitulo>Entrega normal, {entregaNormal.texto}</LinhaTitulo>
            <LinhaDetalhe>
              {entregaNormal.custo !== null && entregaNormal.custo !== undefined
                ? entregaNormal.custo > 0
                  ? formatPrice(entregaNormal.custo)
                  : "Frete grátis"
                : null}
            </LinhaDetalhe>
          </Linha>
        )}

        {retirada && (
          <Linha>
            <IconBadge>
              <Storefront style={{ fontSize: "22px" }} />
            </IconBadge>
            <LinhaTitulo>{retirada.texto}</LinhaTitulo>
            <LinhaDetalhe>Retirada na farmácia</LinhaDetalhe>
          </Linha>
        )}

        {!entregaExpressa && !entregaNormal && !retirada && (
          <Aviso>Sem previsão de prazo para este item na farmácia que atende seu CEP.</Aviso>
        )}

        {rodape}
      </Card>
      {painel}
    </>
  );
}
